import { Typography } from "antd";
import { Line, Pie, Column, Bar } from "@ant-design/plots";

// Shared leaf renderers for the analytics charts — scope-agnostic (already-reduced data in,
// chart out), so every card on the platform renders identically.
//
// THE CHARTING CONVENTIONS (every dashboard card follows these):
//  1. One chart answers one question.
//  2. Unbounded nameable categories (companies, questionnaires, rewards, …) render as a
//     share-of-whole RING: the top 10 named plus an honest "Other" remainder in a fixed muted
//     grey (never a palette colour), with the total in the ring centre. Never vertical columns
//     of names.
//  3. Bounded / ordered domains (bands, severity, status) render as COLUMNS with the ordinal
//     order pinned via explicit scale domains.
//  4. Rates (0–100%) render as HORIZONTAL BARS, worst/best-first, domain pinned to 0–100 —
//     never as pies.
//  5. Trends are lines (two series at most), server-bucketed, captioned "By {bucket}"; rate
//     trends pin the y axis to 0–100.
//  6. Triage lists are paged tables, worst-first.
//  7. Red and orange are alarm colours — reserved for risk/warning meanings, never handed out
//     by categorical palettes; domain colours come from the data when they exist (KPI bands).
//  8. Tooltips on everywhere; legends at the bottom; per-slice labels off on dense rings;
//     count axes keep integer ticks.
//  9. Headline strip numbers are locale-formatted, rates carry one decimal, alarm stats are
//     warning-coloured only when non-zero, and proxies carry honesty captions.
// 10. The empty state is MetricChartEmpty; a redundant chart gets dropped, not decorated.
// 11. Content lists are server-paged (standard paginated shape). Capped bars/tables state
//     N of M; rings carry the honest Other. Selects over unbounded catalogs are
//     server-searched — never fixed-page option loads.
export type ChartKind = "line" | "pie" | "ring" | "column";

// Explicit category→colour mapping for charts whose series carry meaning (severity, status, bands):
// `range[i]` colours `domain[i]`.
export interface ChartColorScale {
  domain: string[];
  range: string[];
}

// The semantic colour tokens every card composes its scales from: red/orange are the alarm
// colours (needs action / risk), gold = in flight, green = done/healthy, grey = closed/neutral.
export const ALARM_RED = "#f5222d";
export const WARN_ORANGE = "#fa8c16";
export const WARN_GOLD = "#faad14";
export const OK_GREEN = "#52c41a";
export const NEUTRAL_GREY = "#8c8c8c";

// Severity is risk-coloured wherever it appears: green → gold → orange → red.
export const SEVERITY_COLORS: ChartColorScale = {
  domain: ["Low", "Medium", "High", "Critical"],
  range: [OK_GREEN, WARN_GOLD, WARN_ORANGE, ALARM_RED],
};

// Workflow status colours: red = still open (needs action), gold = being worked, green = done,
// grey = closed out.
export const STATUS_COLORS: ChartColorScale = {
  domain: ["Open", "InProgress", "Resolved", "Closed"],
  range: [ALARM_RED, WARN_GOLD, OK_GREEN, NEUTRAL_GREY],
};

// Categorical palette for named slices — deliberately free of red/orange (alarm colours).
export const CATEGORY_COLORS = [
  "#1677ff",
  "#13c2c2",
  "#52c41a",
  "#722ed1",
  "#eb2f96",
  "#2f54eb",
  "#a0d911",
  "#08979c",
  "#9254de",
  "#4096ff",
];

// The honest remainder slice: fixed muted grey, never shifts with the palette.
const OTHER_COLOR = "#bfbfbf";
const OTHER_LABEL = "Other";

// Legend labels get ellipsized past this length — the tooltip carries the full name.
const LEGEND_LABEL_MAX = 24;
const ellipsizeLegendLabel = (label: string) =>
  label.length > LEGEND_LABEL_MAX ? `${label.slice(0, LEGEND_LABEL_MAX - 1)}…` : label;

// Spider (outside) slice labels sit between the pie and the card edge, so they have far less room
// than a full-width legend — roughly this many characters in the narrowest card. Past it the canvas
// clips the label at its LEADING edge, which is the dangerous failure: "Completed" rendered as
// "mpleted", which reads as corruption rather than as truncation. Ellipsizing ourselves keeps the
// cut visible and at the end, where a reader understands it. The name is recoverable from the
// legend and the tooltip; only the value is unique to this label, so the value is never shortened.
const SLICE_LABEL_MAX = 16;
const ellipsizeSliceLabel = (label: string) =>
  label.length > SLICE_LABEL_MAX ? `${label.slice(0, SLICE_LABEL_MAX - 1)}…` : label;

export function MetricChartEmpty(props: { label?: string }) {
  return (
    <Typography.Title className="block self-center text-center" level={5} type="secondary">
      {props.label ?? "No data found"}
    </Typography.Title>
  );
}

export function renderMetricChart(
  kind: ChartKind,
  trendData: { [day: string]: number },
  groupData: { type: string; value: number }[],
  valueLabel: string,
  height: number = 300,
  yDomain?: [number, number],
) {
  if (kind === "line") {
    const data = Object.entries(trendData)
      .map(([day, value]) => ({ day, value }))
      .sort((a, b) => a.day.localeCompare(b.day));
    if (!data.length) return <MetricChartEmpty />;
    return (
      <Line
        data={data}
        xField="day"
        yField="value"
        height={height}
        point={{ shapeField: "square", sizeField: 4 }}
        scale={{
          x: { padding: 0.5 },
          ...(yDomain ? { y: { domain: yDomain } } : {}),
        }}
        axis={{
          x: { line: true, label: true },
          y: {
            title: valueLabel,
            line: true,
            label: true,
            tickFilter: (d: any) => Number.isInteger(d),
          },
        }}
      />
    );
  }

  if (!groupData.length) return <MetricChartEmpty />;

  if (kind === "column") {
    return (
      <Column
        data={groupData}
        xField="type"
        yField="value"
        height={height}
        scale={{ x: { padding: 0.5 } }}
        axis={{
          y: { line: true, title: valueLabel, tickFilter: (d: any) => Number.isInteger(d) },
          x: { line: true },
        }}
      />
    );
  }

  // pie / ring
  return (
    <Pie
      data={groupData}
      height={height}
      angleField="value"
      colorField="type"
      // 0.8 left no room for the spider labels: they overflowed the plot and the canvas cut them
      // off at the left edge, so the first characters simply vanished. The pie gives up the width
      // instead, because an unreadable label costs more than a slightly smaller circle.
      radius={0.65}
      innerRadius={kind === "ring" ? 0.5 : undefined}
      label={{
        text: (d: any) => `${ellipsizeSliceLabel(String(d.type))}\n${d.value.toLocaleString()}`,
        position: "spider",
      }}
      legend={{ color: { title: false, position: "bottom", rowPadding: 5 } }}
    />
  );
}

// Share-of-whole ring for unbounded nameable categories: the named groups (already top-N,
// ranked by the server) plus an honest muted-grey "Other" remainder when `total` exceeds the
// named sum. The whole is shown in the ring centre; slice labels stay off (the tooltip and
// legend carry the detail).
export function renderShareRing(
  groups: { type: string; value: number }[],
  total?: number,
  height: number = 260,
) {
  const named = groups.filter((g) => g.value > 0);
  const namedSum = named.reduce((s, g) => s + g.value, 0);
  const other = total != null && total > namedSum ? total - namedSum : 0;
  const data = other > 0 ? [...named, { type: OTHER_LABEL, value: other }] : named;
  if (!data.length) return <MetricChartEmpty />;

  const colors: ChartColorScale = {
    domain: data.map((d) => d.type),
    range: data.map((d, i) =>
      d.type === OTHER_LABEL ? OTHER_COLOR : CATEGORY_COLORS[i % CATEGORY_COLORS.length],
    ),
  };
  const whole = total != null ? total : namedSum;

  return (
    <Pie
      data={data}
      height={height}
      angleField="value"
      colorField="type"
      radius={0.9}
      innerRadius={0.6}
      label={false}
      scale={{ color: colors }}
      legend={{
        color: {
          title: false,
          position: "bottom",
          rowPadding: 5,
          // Long names get ellipsized in the legend (the tooltip keeps the full name) and the
          // legend pages rather than growing unbounded when the named groups still overflow.
          labelFormatter: (d: string) => ellipsizeLegendLabel(String(d)),
          maxRows: 3,
        },
      }}
      annotations={[
        {
          type: "text",
          style: {
            text: whole.toLocaleString(),
            x: "50%",
            y: "50%",
            textAlign: "center",
            fontSize: 20,
            fontWeight: "bold",
            fill: "#595959",
          },
        },
      ]}
    />
  );
}

// Horizontal bars for rates (0–100%): order as delivered (the server ranks worst/best-first),
// value domain pinned to the full scale so half-empty bars read honestly.
export function renderRateBar(
  items: { label: string; rate: number }[],
  height: number = 260,
) {
  if (!items.length) return <MetricChartEmpty />;
  const data = items.map((i) => ({
    type: i.label,
    value: Math.round(i.rate * 10) / 10,
  }));
  return (
    <Bar
      data={data}
      xField="type"
      yField="value"
      height={height}
      legend={false}
      scale={{
        x: { padding: 0.4, domain: data.map((d) => d.type) },
        y: { domain: [0, 100] },
      }}
      axis={{
        x: { line: true },
        y: { line: true, labelFormatter: (d: any) => `${d}%` },
      }}
    />
  );
}

// Two-series trend line (e.g. created vs resolved, earned vs spent) from long-form rows.
export function renderDualTrend(
  data: { date: string; type: string; value: number }[],
  valueLabel: string,
  height: number = 260,
) {
  if (!data.length) return <MetricChartEmpty />;
  return (
    <Line
      data={data}
      xField="date"
      yField="value"
      colorField="type"
      height={height}
      point={{ shapeField: "square", sizeField: 4 }}
      scale={{ x: { padding: 0.5 } }}
      axis={{
        x: { line: true, label: true },
        y: {
          title: valueLabel,
          line: true,
          label: true,
          tickFilter: (d: any) => Number.isInteger(d),
        },
      }}
      legend={{ color: { title: false, position: "bottom", rowPadding: 5 } }}
    />
  );
}

// Stacked column: categorical x with one stacked segment per series. The x domain is pinned to
// the data's delivered order (band/severity orders are meaningful, not alphabetical).
export function renderStackedColumn(
  data: { type: string; series: string; value: number }[],
  valueLabel: string,
  colors?: ChartColorScale,
  height: number = 260,
) {
  if (!data.length) return <MetricChartEmpty />;
  return (
    <Column
      data={data}
      xField="type"
      yField="value"
      colorField="series"
      stack={true}
      height={height}
      scale={{
        x: { padding: 0.5, domain: Array.from(new Set(data.map((d) => d.type))) },
        ...(colors ? { color: colors } : {}),
      }}
      axis={{
        y: { line: true, title: valueLabel, tickFilter: (d: any) => Number.isInteger(d) },
        x: { line: true },
      }}
      legend={{ color: { title: false, position: "bottom", rowPadding: 5 } }}
    />
  );
}

// Column with a colour per category (x labels already name the categories, so no legend).
export function renderCategoryColumn(
  data: { type: string; value: number }[],
  valueLabel: string,
  colors: ChartColorScale,
  height: number = 260,
) {
  if (!data.length) return <MetricChartEmpty />;
  return (
    <Column
      data={data}
      xField="type"
      yField="value"
      colorField="type"
      height={height}
      legend={false}
      scale={{ x: { padding: 0.5 }, color: colors }}
      axis={{
        y: { line: true, title: valueLabel, tickFilter: (d: any) => Number.isInteger(d) },
        x: { line: true },
      }}
    />
  );
}
